import { HTTPAgentService, FHIRSearch } from "adapters/http";
import { getTenantFromToken } from "config/helpers/common_helper";

const requireTenant = () => {
  const tenantId = getTenantFromToken();
  if (!tenantId) throw new Error("Tenant not resolved");
  return tenantId;
};

// Campaigns run on samaras-api-service (intake-router), reached through the
// proxied /api/ai/v1 client (HTTPAgentService) — the same client and path style
// as the subject-create / bulk-upload flow in ehr.service.js (no leading slash,
// so the path is appended to the REACT_APP_SERVER_URL base verbatim).
const campaignBase = () => `intake/tenant/${requireTenant()}/campaign`;

// FHIR Composition type token identifying a campaign (intake-task-job-schedule).
const CAMPAIGN_COMPOSITION_TYPE =
  "http://enliv.com/fhir/coding/composition/type|intake-task-job-schedule";

// True if a campaign Composition already exists with this exact title.
// Used by the wizard to block duplicate campaign names. Blank names short-circuit
// to false (the form's `required` rule covers emptiness). Rejections propagate so
// the caller can decide how to handle a failed check.
export const campaignNameExists = async (name) => {
  const title = (name || "").trim();
  if (!title) return false;
  const params =
    `type=${encodeURIComponent(CAMPAIGN_COMPOSITION_TYPE)}` +
    `&title:exact=${encodeURIComponent(title)}` +
    `&_count=1`;
  const res = await FHIRSearch("Composition", params);
  const total = res?.data?.total ?? res?.data?.entry?.length ?? 0;
  return total > 0;
};

export const listCampaigns = async (filters = {}, pageToken, pageSize) => {
  const params = { ...filters };
  if (pageToken) params.pageToken = pageToken;
  if (pageSize) params.pageSize = pageSize;
  const res = await HTTPAgentService.get(campaignBase(), { params });
  return res.data;
};

export const launchCampaign = async (payload) => {
  const res = await HTTPAgentService.post(campaignBase(), payload);
  return res.data;
};

export const cancelCampaign = async (id) => {
  const res = await HTTPAgentService.post(`${campaignBase()}/${id}/cancel`, {});
  return res.data;
};

// Cancel a single subject's outreach within a campaign (per-patient slice of campaign cancel):
// cancels their campaign Tasks (preserving final outcomes) and deletes their pending Cloud Tasks.
// `campaignFhirId` is the campaign Composition _id; `patientId` is the bare FHIR Patient id.
export const cancelCampaignSubject = async (campaignFhirId, patientId) => {
  const res = await HTTPAgentService.post(
    `${campaignBase()}/${campaignFhirId}/subjects/${patientId}/cancel`,
    {}
  );
  return res.data;
};

// Enroll a single subject into a campaign (create-or-reactivate). On the task detail
// page this re-enrolls a dormant/failed subject so outreach resumes. `campaignFhirId`
// is the campaign Composition _id; `patientId` is the bare FHIR Patient id.
// Returns { campaignId, patientId, taskId, outcome, message }.
export const enrollCampaignSubject = async (campaignFhirId, patientId) => {
  const res = await HTTPAgentService.post(
    `${campaignBase()}/${campaignFhirId}/subjects/${patientId}/enroll`,
    {}
  );
  return res.data;
};

export const getCampaign = async (id) => {
  const res = await HTTPAgentService.get(`${campaignBase()}/${id}`);
  return res.data;
};

export const getCampaignSummary = async () => {
  const res = await HTTPAgentService.get(`${campaignBase()}/summary`);
  return res.data?.result ?? res.data;
};

export const getCampaignDetailSummary = async (compositionId) => {
  const res = await HTTPAgentService.get(
    `${campaignBase()}/${compositionId}/detail-summary`
  );
  return res.data?.result ?? res.data;
};

export const listCampaignSubjects = async (
  compositionId,
  filters = {},
  pageToken,
  pageSize
) => {
  const params = { ...filters };
  if (pageToken) params.pageToken = pageToken;
  if (pageSize) params.pageSize = pageSize;
  const res = await HTTPAgentService.get(
    `${campaignBase()}/${compositionId}/subjects`,
    { params }
  );
  return res.data?.result ?? res.data;
};

// Locked default lead-gen questions. The served items are global/English-only, but the
// route is tenant-scoped for auth context (verifyAuth forwards the tenant to the auth
// service; the FE conveys tenant via the URL path, not a header). The wizard marks the
// returned items locked client-side.
export const getLeadGenDefaultQuestions = async () => {
  const res = await HTTPAgentService.get(`intake/tenant/${requireTenant()}/lead-gen/default-questions`);
  return res.data?.items || [];
};
